// src/app/(rotas)/login/_components/LoginBackground.tsx
"use client";

import React from "react";
import { motion } from "motion/react";
import { Logo } from "visuais";

const PARTICLE_COUNT = 40;
const PARTICLE_TYPES = [
  "w-2 h-2 dark:bg-indigo-900/80 bg-indigo-200/80",
  "w-2 h-2 dark:bg-teal-900/80 bg-teal-200/80",
  "w-2 h-2 dark:bg-blue-900/80 bg-blue-200/80",
];

function getRandomParticleProps() {
  return {
    left: Math.random() * 100,
    top: Math.random() * 100,
    opacity: Math.random() * 0.6 + 0.4,
    type: PARTICLE_TYPES[Math.floor(Math.random() * PARTICLE_TYPES.length)],
    x: Math.random() * 300 - 150,
    y: Math.random() * 300 - 150,
    rotate: Math.random() * 360,
    scale: Math.random() * 1.5 + 0.5,
    duration: Math.random() * 15 + 10, // 10s a 25s
    delay: Math.random() * 2,
  };
}

type Particle = {
  left: number;
  top: number;
  opacity: number;
  type: string;
  x: number;
  y: number;
  rotate: number;
  scale: number;
  duration: number;
  delay: number;
};

export function Background() {
  const [particles, setParticles] = React.useState<Particle[]>([]);

  React.useEffect(() => {
    setParticles(
      Array.from({ length: PARTICLE_COUNT }, getRandomParticleProps)
    );
  }, []);

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none ">
      {/* Logo animado de fundo */}
      <motion.div
        className="absolute -top-100 -right-100 opacity-10 pointer-events-none"
        initial={{ scale: 0, rotate: 180, opacity: 0 }}
        animate={{
          scale: [0, 1, 1.03, 1],
          rotate: [180, 0, 360],
          opacity: [0, 1, 1, 1],
        }}
        transition={{
          duration: 1.5,
          times: [0, 0.5, 0.8, 1],
          ease: "easeOut",
        }}
        whileInView={{
          rotate: [0, 360],
          transition: { duration: 60, repeat: Infinity, ease: "linear" },
        }}
      >
        <Logo className="size-350 opacity-5" color="var(--primary)" />
      </motion.div>


      {/* Partículas de segurança */}
      <div className="absolute inset-0 pointer-events-none z-0 opacity-50">
        {particles.map((p, i) => (
          <motion.div
            key={i}
            className={`security-particle absolute rounded-full ${p.type}`}
            style={{ left: `${p.left}%`, top: `${p.top}%`, opacity: p.opacity }}
            animate={{
              x: [0, p.x, 0],
              y: [0, p.y, 0],
              rotate: [0, p.rotate, 0],
              scale: [1, p.scale, 1],
              opacity: [p.opacity, p.opacity * 0.7, p.opacity],
            }}
            transition={{
              duration: p.duration,
              repeat: Infinity,
              repeatType: "reverse",
              delay: p.delay,
              ease: "easeInOut",
            }}
          />
        ))}
      </div>

      {/* Grid de fundo sutil */}
      <div className="absolute inset-0 opacity-10">
        <div
          className="w-full h-full"
          style={{
            backgroundImage: `
              linear-gradient(rgba(34, 197, 94, 0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(34, 197, 94, 0.1) 1px, transparent 1px)
            `,
            backgroundSize: "50px 50px",
          }}
        ></div>
      </div>

      {/* Efeitos de luz nos cantos */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl animate-pulse"/>
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse delay-1000"/>

      {/* Overlay de luz ambiente */}
      <div className="absolute inset-0 bg-gradient-to-t from-transparent via-transparent to-white/5 pointer-events-none"/>
    </div>
  );
}
